import React, { useState, useEffect, useRef } from 'react';

import {text} from '../../text';
import {hooks} from '../../hooks';
import {utils} from '../../utils';
import {custom} from '../../custom';
import {svg} from '../../assets/svg';
import {theme} from '../../constants';
import {components} from '../../components';
import axiosInstance from '../../services/axiosInstance';
import { formatDateTime } from '../../utils/formatText';
import {socketService} from '../../services';
import { listCache } from '../../utils/listCache';
import { useLanguage } from '../../context/LanguageContext';
import { MdCheckCircle, MdError, MdWarning, MdInfo, MdNotifications } from 'react-icons/md';
import InfiniteScroll from 'react-infinite-scroll-component';

// Fix untuk error TS2786: 'Icon' cannot be used as a JSX component
const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;
const InfoIcon = MdInfo as any;
const NotificationsIcon = MdNotifications as any;

export const Notification: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const authSlice = hooks.useAppSelector((state: any) => state.authSlice);
  const { t } = useLanguage();
  const initialCache = listCache.restore('notifications');
  const [notifications, setNotifications] = useState<any[]>(initialCache?.items ?? []);
  const [offset, setOffset] = useState(initialCache?.cursor ?? 0);
  const [hasMore, setHasMore] = useState(initialCache?.hasMore ?? true);
  const [isLoading, setIsLoading] = useState(!initialCache);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filterActionRequired, setFilterActionRequired] = useState(false);

  const isFirstOffsetRun = useRef(true);
  const scrollTopRef = useRef(initialCache?.scrollTop ?? 0);

  // silent=true: fetch di background tanpa tampilkan loading spinner (untuk refresh setelah restore cache)
  const fetchNotifications = async (currentOffset: number = 0, silent = false) => {
    try {
      if (!silent) {
        if (currentOffset === 0) setIsLoading(true);
        else setIsLoadingMore(true);
      }

      const res = await axiosInstance.get(`/notifications?limit=20&offset=${currentOffset}`);
      const newData = res.data;

      if (newData.length < 20) setHasMore(false);
      else setHasMore(true);

      if (currentOffset === 0) {
        setNotifications(newData);
      } else {
        setNotifications((prev) => {
          const existingIds = new Set(prev.map(n => n.id));
          const uniqueNew = newData.filter((n: any) => !existingIds.has(n.id));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (error) {
      console.error('Failed to fetch notifications', error);
      if (currentOffset === 0 && !silent) setNotifications([]);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (notifications.length === 0) return;
    listCache.save('notifications', { items: notifications, cursor: offset, hasMore, scrollTop: scrollTopRef.current });
  }, [notifications, offset, hasMore]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialCache) {
      // Restore scroll position dulu agar user kembali ke posisi yang sama
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById('scrollableDiv');
          if (el && initialCache.scrollTop > 0) {
            el.scrollTop = initialCache.scrollTop;
            scrollTopRef.current = initialCache.scrollTop;
          }
        });
      });
      // Selalu refresh data di background — tampilkan stale items dulu, replace saat selesai
      listCache.clear('notifications');
      fetchNotifications(0, true);
    } else {
      fetchNotifications(0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authSlice?.isAuthenticated) return;

    const handleNewNotification = (notification: any) => {
      if (notification.user_id == authSlice?.userId) {
        setNotifications((prev) => [notification, ...prev]);
      }
    };

    socketService.on('new_notification', handleNewNotification);
    return () => {
      socketService.off('new_notification', handleNewNotification);
    };
  }, [authSlice?.isAuthenticated, authSlice?.userId]);

  useEffect(() => {
    if (isFirstOffsetRun.current) { isFirstOffsetRun.current = false; return; }
    if (offset > 0) fetchNotifications(offset);
  }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNotificationClick = async (notif: any) => {
    let latestNotifications = notifications;

    if (!notif.is_read) {
      try {
        await axiosInstance.put(`/notifications/${notif.id}/read`);
        latestNotifications = notifications.map(n => n.id === notif.id ? { ...n, is_read: true } : n);
        setNotifications(latestNotifications);
      } catch (e) {
        console.error('Failed to mark as read', e);
      }
    }

    // Save cache sebelum navigate — useEffect tidak sempat jalan saat komponen unmount
    listCache.save('notifications', { items: latestNotifications, cursor: offset, hasMore, scrollTop: scrollTopRef.current });

    if (notif.data?.url) {
      navigate(notif.data.url);
    } else if (notif.data?.taskId) {
      navigate(`/TaskDetails?id=${notif.data.taskId}${notif.data.scrollToComments ? '&comments=1' : ''}`);
    } else if (notif.data?.leaveId) {
      navigate(`/LeaveDetails?id=${notif.data.leaveId}`);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axiosInstance.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      window.dispatchEvent(new CustomEvent('notifications-all-read'));
    } catch (e) {
      console.error('Failed to mark all as read', e);
    }
  };

  const getIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'success': return <CheckCircleIcon size={24} color="#10B981" />;
      case 'reject': case 'error': case 'alert': return <ErrorIcon size={24} color="#EF4444" />;
      case 'warning': return <WarningIcon size={24} color="#F59E0B" />;
      case 'info':
      default: return <InfoIcon size={24} color="#3B82F6" />;
    }
  };

  const renderBackground = (): JSX.Element => {
    return <components.Background />;
  };

  const renderBottomTabBar = () => {
    return <components.BottomTabBar />;
  };

  const renderContent = (): JSX.Element => {
    return (
      <main
        id="scrollableDiv"
        onScroll={(e) => { scrollTopRef.current = (e.currentTarget as HTMLElement).scrollTop; }}
        style={{marginTop: 20, paddingBottom: 100, height: 'calc(100vh - 16px)', overflowY: 'auto'}}
        className='container'
      >
        <div style={{
          position: 'sticky', top: 0, backgroundColor: '#FFFFFF',
          zIndex: 10, paddingBottom: 12, paddingTop: 4,
        }}>
          <div style={{ ...utils.rowCenterSpcBtw() }}>
            <text.H2>{t.notification.title}</text.H2>
            {notifications.some(n => !n.is_read) && !filterActionRequired && (
              <div onClick={handleMarkAllRead} style={{ cursor: 'pointer' }}>
                <text.T14 style={{ color: theme.colors.mainColor, fontWeight: 'bold' }}>
                  {t.notification.markAllRead}
                </text.T14>
              </div>
            )}
          </div>
          <div style={{ marginTop: 10 }}>
            <div
              onClick={() => setFilterActionRequired(prev => !prev)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
                border: `1.5px solid ${filterActionRequired ? '#F59E0B' : '#E5E7EB'}`,
                backgroundColor: filterActionRequired ? '#FEF3C7' : '#F9FAFB',
              }}
            >
              <div style={{
                width: 8, height: 8, borderRadius: 4,
                backgroundColor: filterActionRequired ? '#F59E0B' : '#D1D5DB',
              }} />
              <text.T12 style={{
                fontWeight: '700',
                color: filterActionRequired ? '#92400E' : '#6B7280',
              }}>
                {(t.notification as any).actionRequired}
              </text.T12>
            </div>
          </div>
        </div>

        {(() => {
          const displayList = filterActionRequired
            ? notifications.filter(n => n.data?.action_required === true)
            : notifications;

          return isLoading ? (
            <text.T16 style={{ textAlign: 'center', marginTop: 40, color: '#9CA3AF' }}>{t.common.loading}</text.T16>
          ) : displayList.length === 0 ? (
            <div style={{ textAlign: 'center', marginTop: 60, opacity: 0.5 }}>
              <NotificationsIcon size={60} color="#9CA3AF" />
              <text.T16 style={{ marginTop: 10 }}>
                {filterActionRequired ? (t.notification as any).noActionRequired : t.notification.noNotifications}
              </text.T16>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={notifications.length}
              next={() => setOffset(prev => prev + 20)}
              hasMore={!filterActionRequired && hasMore}
              loader={
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  <text.T14 style={{ color: '#6B7280', textAlign: 'center' }}>{t.leaveHistory?.loadingMore || 'Loading more...'}</text.T14>
                </div>
              }
              endMessage={
                <div style={{ textAlign: 'center', padding: '10px 0', marginBottom: 20 }}>
                  <text.T14 style={{ color: '#6B7280', textAlign: 'center' }}>{t.leaveHistory?.allDataLoaded || 'All notifications loaded'}</text.T14>
                </div>
              }
              scrollableTarget="scrollableDiv"
              style={{ overflow: 'visible' }}
            >
              {displayList.map((notification, index, array) => {
                const isLast = index === array.length - 1;
                const isActionRequired = notification.data?.action_required === true;
                return (
                  <div
                    key={notification.id}
                    style={{
                      padding: 12,
                      borderRadius: 10,
                      border: notification.is_read ? '1px solid #F3F4F6' : `1px solid ${theme.colors.mainColor}40`,
                      marginBottom: isLast ? 0 : 10,
                      backgroundColor: notification.is_read ? theme.colors.white : '#FFF7F2',
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {!notification.is_read && (
                      <div style={{
                        position: 'absolute', top: 10, right: 10,
                        width: 8, height: 8, borderRadius: 4, backgroundColor: theme.colors.mainColor,
                      }} />
                    )}
                    <div style={{...utils.rowCenter({gap: 8})}}>
                      <div>{getIcon(notification.type)}</div>
                      <text.H5 style={{ flex: 1, paddingRight: 10, fontSize: 14 }} numberOfLines={1}>
                        {notification.title}
                      </text.H5>
                      {isActionRequired && (
                        <span style={{
                          fontSize: 10, fontWeight: '700', padding: '2px 7px',
                          borderRadius: 999, backgroundColor: '#FEF3C7',
                          color: '#92400E', border: '1px solid #FDE68A',
                          flexShrink: 0,
                        }}>
                          {(t.notification as any).actionTag}
                        </span>
                      )}
                    </div>
                    {notification.description && (
                      <text.T16 style={{marginTop: 6, color: theme.colors.bodyTextColor, lineHeight: 1.5, fontSize: 14}}>
                        {notification.description}
                      </text.T16>
                    )}
                    <div
                      style={{
                        fontSize: 10,
                        color: '#9CA3AF',
                        lineHeight: 1.6,
                        ...theme.fonts.SourceSansPro_400Regular,
                        marginTop: 8,
                      }}
                    >
                      {formatDateTime(notification.created_at)}
                    </div>
                  </div>
                );
              })}
            </InfiniteScroll>
          );
        })()}
      </main>
    );
  };

  return (
    <div id='screen'>
      {renderBackground()}
      {renderContent()}
      {renderBottomTabBar()}
    </div>
  );
};
